import { useEffect, useMemo, useState } from 'react';
import { difference, isEmpty } from 'lodash';
import { Button, Grid, Popover } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { LoadingButton } from '@mui/lab';

import TextField from 'common/forms/TextField';
import { SortKey } from 'enums/common.enum';
import { casingFormat } from 'common/utils';
import { validateMinLength } from 'common/forms/formValidations';
import { updateRxIntakeRequestFilter } from 'store/thunks/workQueueThunks';
import { getPartnerPractices } from 'store/thunks/partnerThunks';

import MultiPracticeSelectField from 'common/forms/MultiPracticeSelectField';

const RxInTakeRequestsFilterButton = () => {
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [practices, setPractices] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);

  const [formInitialValues, setFormInitialValues] = useState({});

  const filter = useSelector(({ workQueue: { rxIntakeRequests } }) => rxIntakeRequests.filter);
  const loading = useSelector((state) => state.workQueue.rxIntakeRequests.loading);

  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const partnerOptions = useMemo(
    () =>
      partners?.map(({ partnerName, partnerUuid }) => ({
        value: partnerUuid,
        label: casingFormat(partnerName),
      })) ?? [],
    [partners]
  );

  const practiceOptions = useMemo(
    () =>
      practices?.map(({ practiceId, practiceName }) => ({
        value: practiceId,
        label: casingFormat(practiceName),
      })) ?? [],
    [practices]
  );

  useEffect(() => {
    if (isEmpty(selectedPartnerIds)) {
      setPractices([]);
      return;
    }

    dispatch(getPartnerPractices({ partnerIds: selectedPartnerIds })).then((practice) => {
      if (practice) {
        setPractices(practice);
      }
    });
  }, [selectedPartnerIds, dispatch]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);

    const selectedPartners = partnerOptions.filter((partner) =>
      filter?.partnerIds?.includes(partner.value)
    );

    const partnerIds = selectedPartners.map((partner) => partner.value);
    if (!isEmpty(partnerIds)) {
      setSelectedPartnerIds(partnerIds);
    }

    const selectedPractices = practiceOptions.filter((practice) =>
      filter?.practiceIds?.includes(practice.value)
    );

    setFormInitialValues({
      ...filter,
      partnerIds: selectedPartners,
      practiceIds: selectedPractices,
    });
  };

  const handleClose = () => setAnchorEl(null);

  const popoverId = anchorEl ? 'WorkQueue-ReviewInClaimEligibilityFilterButton-Popover' : undefined;

  const handlePartnerChange = (newValue, form) => {
    const partnerIds = newValue.map((partner) => partner.value);
    const removedPartnerIds = difference(selectedPartnerIds, partnerIds);

    setSelectedPartnerIds(partnerIds);

    if (!isEmpty(removedPartnerIds)) {
      const currentPractices = form.getState().values[SortKey.PRACTICE_IDS] || [];

      const updatedPractices = currentPractices.filter(({ value: practiceId }) => {
        const practice = practices.find((p) => p.practiceId === practiceId);
        return practice && !removedPartnerIds.includes(practice.partnerId);
      });

      form.change(SortKey.PRACTICE_IDS, updatedPractices);
    }

    form.change(SortKey.PARTNER_IDS, newValue);

    if (isEmpty(partnerIds)) {
      setPractices([]);
      form.change(SortKey.PRACTICE_IDS, []);
      return;
    }

    setSelectedPartnerIds(partnerIds);
  };

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    dispatch(
      updateRxIntakeRequestFilter({
        filter: {
          ...formData,
          requestedDate: formData.requestedDate && formData.requestedDate.toISOString(),
          partnerIds: !isEmpty(formData.partnerIds)
            ? formData.partnerIds?.map((partner) => partner.value)
            : undefined,
          practiceIds: !isEmpty(formData.practiceIds)
            ? formData.practiceIds.map((practice) => practice.value)
            : undefined,
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={formInitialValues}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid, form }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Patient'
                      name={SortKey.PATIENT_NAME}
                      id='WorkQueue-RxInTakeRequestsFilterButton-patientName-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Drug Name'
                      name={SortKey.MEDICATION_NAME}
                      id='WorkQueue-RxInTakeRequestsFilterButton-mediationName-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <MultiPracticeSelectField
                    label='Partner'
                    name={SortKey.PARTNER_IDS}
                    id='WorkQueue-RxInTakeRequestsFilterButton-Partner-dropdown'
                    options={partnerOptions}
                    onChange={(event, newValue) => {
                      handlePartnerChange(newValue, form);
                    }}
                  />

                  <MultiPracticeSelectField
                    label='Practice'
                    name={SortKey.PRACTICE_IDS}
                    id='WorkQueue-RxInTakeRequestsFilterButton-practice-dropdown'
                    disabled={isEmpty(form.getState().values[SortKey.PARTNER_IDS])}
                    options={practiceOptions}
                  />

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Partner Account'
                      name={SortKey.PARTNER_ACCOUNT}
                      id='WorkQueue-RxInTakeRequestsFilterButton-partnerAccount-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Script Id'
                      name={SortKey.SCRIPT_ID}
                      id='WorkQueue-RxInTakeRequestsFilterButton-scriptId-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Docutrack Number'
                      name={SortKey.DOCUTRACK_ID}
                      id='WorkQueue-RxInTakeRequestsFilterButton-docuTrack-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default RxInTakeRequestsFilterButton;
